import { defaultsByBlockType } from 'shared-helpers/consts';

import type {
  Block,
  BlockMultipleChoice,
  Course,
  LocalizeBlock,
  LocalizeBlockData
} from 'shared-types';

type DefaultLocale =
  (typeof defaultsByBlockType)[Block['type']]['defaultLocaleData'];

type LocalesType = NonNullable<Block['data']>['locales'];

type BaseInputBlock = {
  data: { locales: LocalesType } | null;
  type: Block['type'];
  draftData?: { locales: LocalesType } | null;
  draftType?: Block['type'] | null;
};

interface Params<T> {
  block: T;
  languageDefault: Course['language'];
  languageTarget: Course['language'];
}

const toPlain = <U>(value: U): U => {
  const maybe = value as unknown as
    | { get?: (opts: { plain: true }) => U }
    | null
    | undefined;
  if (maybe && typeof maybe.get === 'function') {
    return maybe.get({ plain: true });
  }
  return value;
};

const translatableRegex =
  /^\{\{\s*locales\.translatable\.([a-zA-Z0-9_]+)\s*\}\}$/;
const localeRegex = /^\{\{\s*locales\.([a-zA-Z0-9_]+)\s*\}\}$/;

const getLocalizedBlock = <T extends BaseInputBlock>({
  block,
  languageDefault,
  languageTarget
}: Params<T>): LocalizeBlock<T> => {
  // Normalize source to a plain object to support direct sequelize instances
  const source: typeof block = toPlain(block);

  // helper to get defaults for a type
  const getDefaultsForType = (data: T['data'], type: T['type']) => {
    const isSingleChoice =
      type === 'multiplechoice' &&
      Boolean((data as BlockMultipleChoice)?.isSingleChoice);

    const canonicalType = isSingleChoice ? 'singlechoice' : type;

    const { defaultLocaleData } = defaultsByBlockType[canonicalType] || {};
    const baseDefaults = (defaultLocaleData ?? {}) as DefaultLocale;

    return baseDefaults;
  };

  /**
   * Recursively localize a value by resolving placeholder strings in nested
   * objects and arrays. Supports:
   * - {{ locales.translatable.X }} → locales.translatable[X] || ''
   * - {{ locales.X }} → locales[X] || defaults[X] || null
   */
  const localizeValue = (
    value: unknown,
    locales: Record<string, unknown> & {
      translatable?: Record<string, string>;
    },
    defaults = {} as DefaultLocale
  ): unknown => {
    const coerceToDefaultType = (
      maybeValue: unknown,
      defaultValue: unknown
    ): unknown => {
      const defaultType = typeof defaultValue;
      if (defaultValue === null) return maybeValue;
      if (defaultType === 'number') {
        if (typeof maybeValue === 'number') return maybeValue;
        if (typeof maybeValue === 'string') {
          const n = Number(maybeValue);
          return Number.isFinite(n) ? n : maybeValue;
        }
        return maybeValue;
      }
      if (defaultType === 'boolean') {
        if (typeof maybeValue === 'boolean') return maybeValue;
        if (typeof maybeValue === 'string') {
          if (maybeValue.toLowerCase() === 'true') return true;
          if (maybeValue.toLowerCase() === 'false') return false;
        }
        return maybeValue;
      }
      // for strings, if maybeValue is null return defaultValue
      return maybeValue ?? defaultValue;
    };

    if (typeof value === 'string') {
      const trimmed = value.trim();

      const translatableMatch = trimmed.match(translatableRegex);
      if (translatableMatch) {
        const key = translatableMatch[1];
        const text = locales?.translatable?.[key];
        return typeof text === 'string' ? text : '';
      }

      const localeMatch = trimmed.match(localeRegex);
      if (localeMatch) {
        const key = localeMatch[1];
        const hasLocale = Object.prototype.hasOwnProperty.call(locales, key);
        const hasDefault = Object.prototype.hasOwnProperty.call(defaults, key);

        if (hasLocale && hasDefault) {
          const localized = locales[key];
          const defaultValue = defaults[key as keyof typeof defaults];
          return coerceToDefaultType(localized, defaultValue);
        }
        if (hasLocale) return locales[key];
        if (hasDefault) return defaults[key as keyof typeof defaults];
        return null;
      }
      return value;
    }

    if (Array.isArray(value)) {
      // traverse array trying to find any placeholder strings inside
      return value.map((item) => localizeValue(item, locales, defaults));
    }

    // the recursion will start effectively from here
    if (value && typeof value === 'object') {
      // traverse object trying to find any placeholder strings inside
      const result: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        // skip locales, since the localized data will be flattened
        if (key === 'locales') continue;

        result[key] = localizeValue(val, locales, defaults);

        // video links have localizations inside themselves
        // so we need to traverse them as well
        if (key === 'links' && Array.isArray(result[key])) {
          result[key] = localizeValue(result[key], locales, defaults);
        }
      }
      return result;
    }
    return value;
  };

  // helper to localize data (entrypoint)
  const localizeData = <D extends { locales: LocalesType }>(
    data: D | null,
    type: Block['type']
  ) => {
    if (!data) return null;

    // get defaults for the type
    const defaults = getDefaultsForType(data, type);

    // if the target language is not the default language, we
    // augment the defaults data with the default language data
    // as long as the localized target data does not have its own source
    const defaultLocaleData = data.locales?.[languageDefault] ?? {};
    const targetLocaleData = data.locales?.[languageTarget] ?? {};

    const hasOwnSource =
      ('file' in targetLocaleData && Boolean(targetLocaleData.file)) ||
      (type === 'embedlink' &&
        'text' in targetLocaleData &&
        Boolean(targetLocaleData.text));

    let augmentedDefaults = defaults;
    if (languageTarget !== languageDefault && !hasOwnSource) {
      augmentedDefaults = { ...defaults, ...defaultLocaleData };
    }

    return localizeValue(
      data,
      targetLocaleData,
      augmentedDefaults
    ) as LocalizeBlockData<D>;
  };

  // localize data
  const localizedData = localizeData(source.data, source.type);

  // localize draftData
  const localizedBlock = { ...source, data: localizedData } as LocalizeBlock<T>;
  if ('draftData' in source) {
    // draftType of single choice is impossible, as you cannot convert from
    // a multiple choice block to a single choice block in the editor
    const type = (source.draftType || source.type) as Block['type'];

    const { defaultLocaleData } = defaultsByBlockType[type] || {};
    const draftDefaults = (defaultLocaleData || {}) as DefaultLocale;

    const draftData = localizeData<NonNullable<T['draftData']>>(
      (source.draftData as NonNullable<T['draftData']>) ?? null,
      source.draftType ?? source.type
    );

    return { ...localizedBlock, draftData } as LocalizeBlock<T>;
  }

  return localizedBlock;
};

export default getLocalizedBlock;
